import { createSlice, createAsyncThunk, current } from '@reduxjs/toolkit';
import axios from 'axios';
import { getCartItems } from 'app/store/auth/cartsSlice';
import { getProducts } from 'app/main/apps/e-commerce/store/productsSlice';
import { checkNullOrUndefined, formatDateString } from 'app/main/apps/common/AuraFunctions';
import format from 'date-fns/format';
import { showMessage } from 'app/store/fuse/messageSlice';
import { getThunkRejectMessage } from 'app/utils/apiError';
import { getOrders } from './ordersSlice';
import logger from 'app/utils/logger';


export const getPrintProductionData = createAsyncThunk(
  'DashboardApp/widgets/getPrintProductionData',
  async (params) => {
    const response = await axios.post(`/api/widgetOrderItemData`, null, { params });
    const data = await response.data.shipping_label;
    return data;
  }
);

export const getOrder = createAsyncThunk('eCommerceApp/order/getOrder', async (params) => {
  const response = await axios.get(`/api/orders/${params.orderId}`);
  const data = await response.data.order;
  return data === undefined ? null : getInitValues(data);
});

export const getCommunicationHistories = createAsyncThunk(
  'eCommerceApp/order/getCommunicationHistories',
  async (payload) => {
    const response = await axios.post('/api/communications/search', payload);
    const data = await response.data;
    return data
  }
);

export const getRelated = createAsyncThunk('eCommerceApp/order/getRelatedDetails', async (id) => {
  const response = await axios.get(`api/orders/${id}`);
  const data = await response.data.order;
  return data
})

export const getOrderDetails = createAsyncThunk(
  'eCommerceApp/order/getOrderDetails',
  async ({ id, params, signal = null }, { dispatch, getState }) => {
    const config = signal ? { params, signal } : { params };
    const response = await axios.get(`/api/orders/${id}`, config);
    const data = await response.data.order;
    return data ? getInitValues(data) : data;
  }
);

export const downloadOrderPdf = createAsyncThunk(
  'eCommerceApp/order/downloadOrderPdf',
  async ({ id, stage, orderNo }, { dispatch, getState }) => {
    try {
      const response = await axios.get(`/api/downloadOrder`, {
        params: { order_id: id },
        responseType: 'blob', // Specify response type as blob for file download
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;

      // Set a filename (you can customize this based on the API response or logic)
      link.setAttribute('download', `${stage}_${orderNo}.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      return true; // Indicate success
    } catch (error) {
      logger.error('Error downloading the PDF:', error);
      throw error;
    }
  }
);


export const getCustomOrder = createAsyncThunk(
  'eCommerceApp/order/getCustomOrder',
  async ({ id }, { dispatch, getState }) => {
    const response = await axios.get(`/api/getCustomOrder/${id}`);
    const data = await response.data.order;
    return data
  }
);

export const getOrderItemDiscount = createAsyncThunk(
  'eCommerceApp/order/getOrderItemDiscount',
  async ({ cid, pid, stage }, { dispatch, getState }) => {
    const response = await axios.get(`/api/productDiscount`, { params: { cid, pid, stage } });
    const data = await response.data;
    return data;
  }
);

export const getDefaultWeight = createAsyncThunk(
  'eCommerceApp/order/getDefaultWeight',
  async (categoryName) => {
    const response = await axios.get('/api/setting', { params: { category: categoryName } })
    const data = await response.data;
    return data
  }
)

export const requestOrder = createAsyncThunk(
  'eCommerceApp/order/requestOrder',
  async (params, { dispatch }) => {
    const response = await axios.post('/api/addOrders', params);
    const data = await response.data;
    dispatch(getCartItems());
    dispatch(getProducts());
    return data;
  }
);

export const saveOrder = createAsyncThunk(
  'eCommerceApp/order/saveOrder',
  async (orderData, { dispatch, getState }) => {
    dispatch(setLoading());
    const { order } = getState().eCommerceApp;
    let endPoint = '/api/orders';
    let params = {}
    // TODO why do we need to send to separate endpoint?
    if ((orderData.isQuote || orderData.stage === 'quote') && !orderData.isQuoteEdit) {
      endPoint = '/api/addQuote';
      if (orderData.id) {
        endPoint = orderData.isQuote ? '/api/requestForQuote' : '/api/updateRfq'
        if (!orderData.isQuote) {
          params = { action_name: orderData.actionName || "OUT_FOR_QUOTE_APPROVAL" };
        }
      }
    }

    if (orderData.stage === 'rfq') {
      const isConverToQuote = orderData.orderType === 'convert-to-quote'
      orderData = {
        ...orderData,
        status: {
          ...orderData.status,
          category: isConverToQuote ? 'quote_status' : 'rfq_status'
        },
        stage: isConverToQuote ? 'quote' : orderData.stage
      }
    }

    let response;
    if (orderData.id) {
      response = await axios.put(`${endPoint}/${orderData.id}`, {
        ...orderData,
      }, { params });
    } else {
      response = await axios.post(endPoint, {
        ...order,
        ...orderData,
      });
    }

    const data = await response.data.order;
    dispatch(resetLoading());
    return getInitValues(data);
  }
);

export const chooseSupplierForArtWork = createAsyncThunk(
  'eCommerceApp/order/chooseSupplierForArtWork',
  async (orderData, { dispatch, getState }) => {
    dispatch(setLoading());
    const response = await axios.put(`/api/requestForQuote/${orderData.id}`, { ...orderData })
    const data = await response.data.order;
    dispatch(resetLoading());
    return getInitValues(data);
  }
);

export const placeReOrder = createAsyncThunk(
  'eCommerceApp/order/reOrder',
  async (reOrderData, { dispatch, getState }) => {
    await axios.post('/api/orders', reOrderData);
  }
);

export const updateOrder = createAsyncThunk(
  'eCommerceApp/order/updateOrder',
  async (inputData, { dispatch, rejectWithValue }) => {
    let params = {}
    if (inputData?.is_complete) {
      params = { is_complete: true }
    }
    if(inputData?.action_name  === "communication") {
      params.action_name = "communication";
    }
    if(inputData?.action_name  === "quick_edit") {
      params.action_name = "quick_edit";
    }
    const response = await axios.put(`/api/orders/${inputData.id}`, inputData, { params });
    // PGSS-5338: the backend refuses a quantity change that would desync an already committed
    // supplier order (invoiced/shipped/paid) and answers HTTP 200 with supplierSyncWarning, a
    // message and supplierConflicts -- but no `order`. Reading response.data.order then yields
    // undefined, the thunk FULFILS, and callers show a success toast for a save that never
    // happened. The refusal is handled here rather than at a call site because every caller
    // inherits it: OrderDialog, ChooseSupplier (PGSS-5336) and the rest would each need the
    // same treatment otherwise, which is how two spellings of one interaction get built.
    if (response.data?.supplierSyncWarning) {
      dispatch(
        showMessage({
          message: response.data.message,
          variant: 'warning',
          anchorOrigin: { vertical: 'top', horizontal: 'right' },
        })
      );
      return rejectWithValue({
        supplierSyncWarning: true,
        message: response.data.message,
        supplierConflicts: response.data.supplierConflicts || [],
      });
    }
    const data = await response.data.order
    return data;
  }
);

// TODO performaction api might be common (need to check) and should be placed in some other slice
export const performAction = createAsyncThunk(
  'eCommerceApp/order/performAction',
  async (payload, { dispatch, rejectWithValue }) => {
    dispatch(setPerformingAction());
    try {
      const response = await axios.put(`/api/performAction`, payload);
      return response.data.order ? response.data.order : response.data;
    } catch (error) {
      // PGSS-5613: hand callers the backend's error body. Without this the thunk rejected with only
      // Axios's "Request failed with status code 400", so a refusal the backend explained (e.g. "Invoice
      // … has payments recorded against it and cannot be cancelled") reached the user as a generic message.
      return rejectWithValue(error?.response?.data ?? { message: getThunkRejectMessage(error) });
    } finally {
      // Always, so a failed action no longer leaves the "performing" flag set.
      dispatch(resetPerformingAction());
    }
  }
);

export const getContactInfo = createAsyncThunk('eCommerceApp/order/getContactInfo', async () => {
  const response = await axios.get(`/api/contactInfo`);
  const data = await response.data;
  return data;
});

export const getQuotedSuppliers = createAsyncThunk('eCommerceApp/order/getQuotedSuppliers', async ({ id, params }, { dispatch }) => {
  dispatch(setQuotedSuppliersListLoading(true))
  const response = await axios.get(`/api/viewSupplier/${id}`, { params });
  dispatch(setQuotedSuppliersListLoading(false))
  const data = await response.data;
  return data
});

export const getInvoiceDetails = createAsyncThunk('eCommerceApp/order/getInvoiceDetails', async (params) => {
  const response = await axios.get(`/api/invoice`, { params });
  const data = await response.data;
  return data
});

export const getBriefInvoiceDetails = createAsyncThunk('eCommerceApp/order/getBriefInvoiceDetails', async ({ id }) => {
  const response = await axios.get(`/api/getInvoiceDetails/${id}`);
  const data = await response.data;
  return data
});

export const recordInvoiceDiscrepancyDecision = createAsyncThunk(
  'eCommerceApp/order/recordInvoiceDiscrepancyDecision',
  async ({ id, decision, note }) => {
    const response = await axios.post(`/api/invoice/${id}/discrepancy-decision`, { decision, note });
    return response.data;
  }
);

/** PGSS-5306 — raise a credit or debit note against a customer invoice. */
export const createInvoiceAdjustmentNote = createAsyncThunk(
  'eCommerceApp/order/createInvoiceAdjustmentNote',
  async ({ id, note_type, amount, reason, discrepancy_decision_id }, { rejectWithValue }) => {
    try {
      const body = { note_type, amount, reason };
      if (discrepancy_decision_id != null) {
        body.discrepancy_decision_id = discrepancy_decision_id;
      }
      const response = await axios.post(`/api/invoice/${id}/adjustment-notes`, body);
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || 'Unable to raise adjustment note');
    }
  }
);

/** PGSS-5304 Track A — sync unissued invoice lines to current order figures. */
export const syncInvoiceFromOrder = createAsyncThunk(
  'eCommerceApp/order/syncInvoiceFromOrder',
  async ({ id }, { rejectWithValue }) => {
    try {
      const response = await axios.post(`/api/invoice/${id}/sync-from-order`);
      return response.data;
    } catch (error) {
      return rejectWithValue(getThunkRejectMessage(error));
    }
  }
);

export const getShippingLabelData = createAsyncThunk('eCommerceApp/order/getShippingLabelData', async (params) => {
  const response = await axios.get(`/api/shipmentLabel`, { params });
  const data = await response.data;
  return data
});

export const convertToOrder = createAsyncThunk(
  'eCommerceApp/order/convertToOrder',
  async (params, { dispatch }) => {
    dispatch(setLoading());
    await axios
      .put(`api/quote/${params.id}`, params)
      .then(() => {
        dispatch(
          showMessage({
            message: 'Quote has been converted to order',
            type: 'success'
          })
        );
      });
  }
);

export const updateShipmentApproval = createAsyncThunk(
  'eCommerceApp/order/updateShipmentApproval',
  async (params, { dispatch }) => {
    dispatch(setLoading());
    await axios
      .put(`api/shipment/${params.id}`, params)
      .then(() => {
        let message = params.is_approve ? 'approved' : 'rejected'
        if(checkNullOrUndefined(params.is_approve))
          message = 'dispatched'
        dispatch(
          showMessage({
            message: `Shipment has been ${message}`,
            type: 'success'
          })
        );
      }).catch(() => dispatch(showMessage({ type: 'error', message: 'Failed to update' })));
  }
);

export const getAdvancePayment = createAsyncThunk(
  'eCommerceApp/order/getAdvancePayment',
  async (id) => {
    const response = await axios.get(`/api/getAdvancePayment/${id}`);
    const data = await response.data;
    return data
  }
);

export const addAdvancePayment = createAsyncThunk(
  'eCommerceApp/order/addAdvancePayment',
  async (payload) => {
    const response = await axios.put(`/api/advancePayment/${payload.id}`, payload);
    const data = await response.data;
    return data;
  }
);

// PGSS-5540: correcting a recorded advance payment. The path takes the PAYMENT id here, not the
// order id that addAdvancePayment above takes -- they are different things on a similar-looking URL.
export const updateAdvancePayment = createAsyncThunk(
  'eCommerceApp/order/updateAdvancePayment',
  async ({ paymentId, payload }) => {
    const response = await axios.put(`/api/advancePayment/update/${paymentId}`, payload);
    const data = await response.data;
    return data;
  }
);

export const deleteAdvancePayment = createAsyncThunk(
  'eCommerceApp/order/deleteAdvancePayment',
  async (paymentId) => {
    const response = await axios.delete(`/api/advancePayment/${paymentId}`);
    const data = await response.data;
    return data;
  }
);

export const sendEmail = createAsyncThunk(
  'eCommerceApp/order/sendEmail',
  async (id) => {
    const response = await axios.get(`/api/sendEmail/${id}`);
    const data = await response.data;
    return data
  }
);


export const sendReminderEmail = createAsyncThunk(
  'eCommerceApp/order/sendReminderEmail',
  async ({id, params}) => {
    const response = await axios.get(`/api/sendReminderEmail/${id}`, {params});
    const data = await response.data;
    return data
  }
);


export const getVasCategoryTypes = createAsyncThunk(
  'eCommerceApp/products/getVasCategoryTypes',
  async () => {
    const response = await axios.get('/api/vasCategoryTypes');
    const data = await response.data;
    return data;
  }
);

export const getVasPriceList = createAsyncThunk('eCommerceApp/order/getVasPriceList', async (params) => {
  const response = await axios.get(`/api/vasPriceLists`, { params });
  const data = await response.data?.data;
  return data
});

export const getRevisionHistory = createAsyncThunk('eCommerceApp/order/getRevisionHistory', async (params) => {
  const response = await axios.get(`/api/getRevisionHistory`, { params });
  const data = await response.data?.revision_history;
  return data
});

// PGSS-5030: approval rounds — flat ordered list (oldest first), replaces the bucketed
// legacy shape for the New Revision Histories view. Rejects until the backend is deployed.
export const getRevisionRounds = createAsyncThunk('eCommerceApp/order/getRevisionRounds', async (orderId) => {
  const response = await axios.get(`/api/orders/${orderId}/revisions`);
  return response.data;
});

// PGSS-5186: Envers snapshot compare for Change History 1b (Staff/Admin only on the API).
export const getChangeHistoryCompare = createAsyncThunk(
  'eCommerceApp/order/getChangeHistoryCompare',
  async ({ orderId, fromRev, toRev }) => {
    const response = await axios.get(`/api/orders/${orderId}/change-history/compare`, {
      params: { fromRev, toRev },
    });
    return response.data;
  }
);

export const getOrderChangeHistory = createAsyncThunk('eCommerceApp/order/getOrderChangeHistory', async (params) => {
  const response = await axios.get(`/api/revisionHistory`, { params });
  const data = await response.data;
  return data
});

export const getComments = createAsyncThunk('eCommerceApp/order/getComments', async (params) => {
  const response = await axios.get(`/api/getComments`, { params });
  const data = await response.data?.comments_history;
  return data
});

export const addComments = createAsyncThunk('eCommerceApp/order/addComments',  async (params) => {
  const response = await axios.post('/api/addComments', params);
  const data = await response.data;
  return data;
});

/**
 * Handles create/updtae communication message
 */
export const storeCommunicationMessage = createAsyncThunk(
  'eCommerceApp/order/storeCommunicationMessage',
  async (payload) => {
    const response = await axios.post('/api/communications', payload);
    return response.data;
  }
);

/**
 * Created, to open popup based on reponse flag
 */
export const showDialog = createAsyncThunk('eCommerceApp/order/showDialog', async (params) => {
  const response = await axios.get(`/api/showDialog`, { params });
  const data = await response.data;
  return data;
});

/**
 * Created, to add internal notes
 */
export const createNote = createAsyncThunk(
  'eCommerceApp/order/createNote',
  async (payload) => {
    const response = await axios.post('/api/notes', payload);
    return response.data;
  }
);

/**
 * Delete VAS
 */
export const deleteVas = createAsyncThunk(
  'eCommerceApp/order/deleteVas',
  async (payload) => {
    const response = await axios.put(`api/deleteVas/${payload.id}`, payload)
    return response.data;
  }
);

const initialValue = {
  props: {
    open: false,
  },
  data: null,
  param: null,
  dashboard: false,
};

export const initState = {
  loading: false,
  performingAction: false,
  disableOrderRequest: false,
  data: null,
  includeOrderItem: null,
  orderDialog: initialValue,
  confirmInvoiceDialog: initialValue,
  viewOrderDialog: initialValue,
  chooseSupplierDialog: initialValue,
  createSupplierOrderDialog: initialValue,
  quotedSuppliersList: {},
  quotedSuppliersListLoading: false,
  vasDialog: initialValue,
  vasPreviewDialog: initialValue,
  vasCategoryTypes: null,
  vasPriceLists: null,
};

function getBalanceAmount(data) {
  return data.balance_amount ? data.balance_amount : data.amount;
}

export function getInitValues(data, type) {
  return {
    id: data && data.id ? data.id : null,
    order_no: data && data.order_no ? data.order_no : '',
    name: data && data.name ? data.name : '',
    legacy_id: data && data.legacy_id ? data.legacy_id : '',
    customer_id: data && data.customer_id ? data.customer_id : '',
    customer: data && data.customer ? data.customer : null,
    supplier: data && data.supplier ? data.supplier : null,
    suppliers: data && data.suppliers ? data.suppliers : null,
    selected_customer: data && data.customer ? { ...data.customer } : null,
    sales_person: data && data?.sales_person,
    date:
      data && data.date
        ? formatDateString(data.date, 'yyyy-MM-dd')
        : format(new Date(), 'yyyy-MM-dd'),
    eta_date: data && data.eta_date ? formatDateString(data.eta_date, 'yyyy-MM-dd') : '',
    quantity: data && data.quantity ? data.quantity : '',
    customer_provider_number: data && data.customer_provider_number ? data.customer_provider_number : '',
    comments: data && data.comments ? data.comments : '',
    decedent: data && data.decedent ? data.decedent : '',
    amount: data && data.amount ? data.amount : '',
    order_details: data && data.order_details ? data.order_details : [],
    status_id: data && data.status_id ? data.status_id : null,
    status: data && data.status ? data.status : null,
    status_change_time: data && data.status_change_time ? data.status_change_time : null,
    status_histories: data && data.status_histories ? data.status_histories : [],
    note_histories: data && data.note_histories ? data.note_histories : [],
    activity_histories: data && data.activity_histories ? data.activity_histories : [],
    child_activity_histories: data && data.child_activity_histories ? data.child_activity_histories : [],
    documents: data && data.uploadedFile ? data.uploadedFile : [],
    payment_details: data && data.payment_details ? data.payment_details : [],
    draft_invoices_pre_tax_total:
      data && data.draft_invoices_pre_tax_total ? data.draft_invoices_pre_tax_total : '',
    finalized_invoices_pre_tax_total:
      data && data.finalized_invoices_pre_tax_total ? data.finalized_invoices_pre_tax_total : '',
    all_invoices_pre_tax_total:
      data && data.all_invoices_pre_tax_total ? data.all_invoices_pre_tax_total : '',
    shipping_cost: data && data.shipping_cost ? data.shipping_cost : '',
    all_invoices_balance: data && data.all_invoices_balance ? data.all_invoices_balance : '',
    paid_amount: data && data.paid_amount ? data.paid_amount : 0,
    balance_amount: data ? getBalanceAmount(data) : 0,
    payment_status: data && data.payment_status ? data.payment_status : '',
    reference_decedent: data && data.reference_decedent ? data.reference_decedent : '',
    decedents: data && data.decedents ? data.decedents : '',
    ship_to: data && data.ship_to ? data.ship_to : '',
    requested_by: data && data.requested_by,
    client_contract_number: data && data.client_contract_number ? data.client_contract_number : '',
    note: data && data.note ? data.note : '',
    tags: data && data.tags ? data.tags : '',
    order_type: data && data.order_type ? { name: data.order_type } : null,
    location: data?.location ? data.location : '',
    weight: data?.order_details && data?.order_details[0]?.weight ? data?.order_details[0]?.weight : '',
    length: data?.order_details && data?.order_details[0]?.length ? data?.order_details[0]?.length : '',
    width: data?.order_details && data?.order_details[0]?.width ? data?.order_details[0]?.width : '',
    height: data?.order_details && data?.order_details[0]?.height ? data?.order_details[0]?.height : '',
    stone_color: data?.order_details && data?.order_details[0]?.product ? data?.order_details[0]?.product?.stone_color : null,
    finish: data?.order_details && data?.order_details[0]?.finish ? data?.order_details[0]?.finish : '',
    polish: data?.order_details && data?.order_details[0]?.polish ? data?.order_details[0]?.polish : '',
    type: data?.order_details && data?.order_details[0]?.product?.type ? data?.order_details[0]?.product?.type : '',
    sub_type:
      data?.order_details && data?.order_details[0]?.product?.sub_type
        ? data?.order_details[0]?.product?.sub_type
        : '',
    product: data?.order_details && data?.order_details[0]?.product ? data?.order_details[0]?.product : '',
    remarks: data && data.remarks ? data.remarks : 'rrrr',
    reference: data && data.reference ? data.reference : '',
    requested_date:
      data && data.eta_date ? formatDateString(data.eta_date, 'yyyy-MM-dd') : '',
    is_drawing_approval: data?.is_drawing_approval,
    is_proof_approval: data?.is_proof_approval,
    location_for_regular: data && data.location_for_regular ? data.location_for_regular : '',
    quantity_history: data?.quantity_history || [],
    customer_collaborators: data?.customer_collaborators?.length > 0 ? data?.customer_collaborators?.map((item) => ({
      ...item,
      name: item?.email,
      nameOpt: `${item?.first_name ?? ''} ${item?.last_name ?? ''}`, // Fallback to empty string if null or undefined
    })) : [],
    include_shipping_cost: data?.include_shipping_cost ?? true,
    // PGSS-5714: left undefined when the API didn't send it (customers never get it), so a customer's save sends
    // nothing and can't turn off what staff set (review of #2771). The switch reads only `=== true`.
    sandblasted_at_plant: data?.sandblasted_at_plant,
    is_revision_request: data ? data?.is_revision_request : false,
    confirmed_order: data ? data?.confirmed_order : false,
    advance_payments: data?.advance_payments,
    // PGSS-5561: this is an allow-list -- a key absent here never reaches the screen, whatever the
    // API sends. Without it the Advance Payments tab offers Add on an invoiced order and the server
    // refuses the click, which is the exact failure the lock exists to prevent.
    advance_payment_locked: data?.advance_payment_locked,
    has_child_orders: data && data?.child_orders && data?.child_orders?.length > 0 ,
    child_status_histories: data && data.child_status_histories ? data.child_status_histories : [],
    child_note_histories: data && data.child_note_histories ? data.child_note_histories : [],
    proof_documents: data && data.proof_documents ? data.proof_documents : [],
    production_documents: data && data.production_documents ? data.production_documents : [],
    product_proof_documents: data && data.product_proof_documents ? data.product_proof_documents : [],
    vas: data && data.vas ? data.vas : null,
    vas_price_details: data && data.vas_price_details ? data.vas_price_details : [],
    status_workflow_action: data?.status_workflow_action || [],
    received_container_number: data && data.received_container_number ? data.received_container_number : null,
    revision_note: data && data.revision_note ? data.revision_note : null,
    reject_note: data && data.reject_note ? data.reject_note : null,
    revision_documents: data && data.revision_documents ? data.revision_documents : null,
    vas_additional_notes: data && data.vas_additional_notes ? data.vas_additional_notes : null,
    // PGSS-5287: must be listed here to survive. getInitValues is a whitelist — a field the API
    // sends but this object does not name never reaches the store, so the VAS pages would read
    // undefined and the Revision Notes section would stay empty, which is the reported defect.
    vas_revision_note: data && data.vas_revision_note ? data.vas_revision_note : null,
    // PGSS-5294: the documents half of that same section, scoped to the VAS round they were decided
    // on. Same whitelist reason as the note above.
    vas_revision_documents:
      data && data.vas_revision_documents ? data.vas_revision_documents : null,
    // PGSS-5287 (third pass): the three above are the phase-agnostic summary the order page's Products
    // tab reads. The VAS screens read these six instead — VAS is two phases, and a production screen
    // answering with the proof's notes is what QA failed on 2026-08-11. Same whitelist reason again:
    // unlisted keys never reach the store, and an absent key reads exactly like an empty section, so
    // omitting one here would look like the fix simply not working.
    vas_proof_note: data && data.vas_proof_note ? data.vas_proof_note : null,
    vas_proof_revision_note:
      data && data.vas_proof_revision_note ? data.vas_proof_revision_note : null,
    vas_proof_revision_documents:
      data && data.vas_proof_revision_documents ? data.vas_proof_revision_documents : null,
    vas_production_note: data && data.vas_production_note ? data.vas_production_note : null,
    vas_production_revision_note:
      data && data.vas_production_revision_note ? data.vas_production_revision_note : null,
    vas_production_revision_documents:
      data && data.vas_production_revision_documents ? data.vas_production_revision_documents : null,
    // PGSS-5293: the product-side pair of the two above, for the Products tab's Revision Details
    // panel. Same whitelist reason — an unlisted key never reaches the store.
    product_revision_note:
      data && data.product_revision_note ? data.product_revision_note : null,
    product_revision_documents:
      data && data.product_revision_documents ? data.product_revision_documents : null,
    sur_charge_percentage:
      data && !checkNullOrUndefined(data.sur_charge_percentage) ? data.sur_charge_percentage : null,
    sur_charge_amount:
      data && !checkNullOrUndefined(data.sur_charge_amount) ? data.sur_charge_amount : null,
    show_received_box_number:
      data && data.show_received_box_number ? data.show_received_box_number : null,
    is_enable_image_approval: data && data.is_enable_image_approval,
  };
}

const orderSlice = createSlice({
  name: 'eCommerceApp/order',
  initialState: initState,
  reducers: {
    resetOrder: (state, action) => {
      state.data = null;
      state.includeOrderItem = null;
    },
    addOrderItem: (state, action) => {
      state.includeOrderItem = action.payload;
    },
    resetOrderItem: (state, action) => {
      state.includeOrderItem = null;
    },
    setLoading: (state, action) => {
      state.loading = true;
    },
    resetLoading: (state, action) => {
      state.loading = false;
    },
    setPerformingAction: (state, action) => {
      state.performingAction = true;
    },
    resetPerformingAction: (state, action) => {
      state.performingAction = false;
    },
    setQuotedSuppliersListLoading: (state, action) => {
      state.quotedSuppliersListLoading = action.payload;
    },
    newOrder: {
      reducer: (state, action) => {
        state.data = action.payload;
      },
      prepare: (type) => ({
        payload: getInitValues(null, type),
      }),
    },
    openOrderDialog: (state, action) => {
      state.orderDialog = {
        props: {
          open: true,
        },
        completeOrderData: action.payload.completeOrderData,
        data: action.payload.data,
        param: action.payload.param,
        stage: action.payload.stage,
      };
    },
    closeOrderDialog: (state, action) => {
      state.orderDialog = {
        props: {
          open: false,
        },
        data: null,
        param: null,
      };
    },
    openConfirmInvoiceDialog: (state, action) => {
      state.confirmInvoiceDialog = {
        props: {
          open: true
        },
        completeOrderData: action.payload.completeOrderData,
        data: action.payload.data,
        param: action.payload.param,
        stage: action.payload.stage,
      }
    },
    closeConfirmInvoiceDialog: (state, action) => {
      state.confirmInvoiceDialog = {
        props: {
          open: false
        },
        data: null,
        param: null,
      }
    },
    openVasDialog: (state, action) => {
      state[action?.payload?.field || "vasDialog"] = {
        props: {
          open: true
        },
        data: action.payload.data,
        completeOrderData: action.payload.completeOrderData,
        param: action.payload.param,
        stage: action.payload.stage,
      }
    },
    closeVasDialog: (state, action) => {
      state[action?.payload?.field || "vasDialog"] = {
        props: {
          open: false
        },
        data: null,
        completeOrderData: null,
        param: null,
      }
    },
    openViewOrderDialog: (state, action) => {
      state.viewOrderDialog = {
        props: {
          open: true,
        },
        stage: action.payload.stage,
        data: action.payload.data,
        param: action.payload.param,
        completeOrderData: action.payload.completeOrderData,
        dashboard: false,
      };
    },
    closeViewOrderDialog: (state, action) => {
      state.viewOrderDialog = {
        props: {
          open: false,
        },
        data: null,
        param: null,
        dashboard: false,
      };
    },
    openChooseSupplierDialog: (state, action) => {
      state.chooseSupplierDialog = {
        props: {
          open: true,
        },
        param: action.payload.param,
        completeOrderData: action.payload.completeOrderData,
        name: action.payload.name,
        status: action.payload.status,
        stage: action.payload.stage,
        addOnParam: action.payload.addOnParam,
        loadOrdersFun: action.payload.loadOrdersFun,
      };
    },
    closeChooseSupplierDialog: (state) => {
      state.chooseSupplierDialog = {
        props: {
          open: false,
        },
        param: null,
      };
      resetOrder()
      state.quotedSuppliersList = {}
    },
    openSupplierShippingDialog: (state, action) => {
      state.createSupplierOrderDialog = {
        props: {
          open: true,
        },
        param: action.payload.param,
        type: action.payload.type,
        completeOrderData: action.payload.completeOrderData,
        data: action.payload.data,
        // Added optionalData to avoid causing issues in the existing code
        optionalData: action.payload.optionalData
      };
    },
    closeSupplierShippingDialog: (state) => {
      state.createSupplierOrderDialog = {
        props: {
          open: false,
        },
        param: null,
        optionalData: null,
      };
      resetOrder()
    },
    updateQuotedSuppliersList: (state, action) => {
      const {
        selectedIndex,
        selectedOrder,
        field,
        value,
        updateVas
      } = action.payload;
      if (updateVas) {
        state.quotedSuppliersList.orders[selectedIndex].vas_price_details = value;
      }
      else if(field === "unit_price") {
        state.quotedSuppliersList.orders[selectedIndex].order_details[selectedOrder].product[field] = value;
      }
      else if(field === "margin") {
        state.quotedSuppliersList.orders[selectedIndex].order_details[selectedOrder][field] = value;
        state.quotedSuppliersList.orders[selectedIndex].order_details[selectedOrder].product[field] = value;
      }
      else {
        state.quotedSuppliersList.orders[selectedIndex].order_details[selectedOrder][field] = value;
      }
    },
    resetCommunicationHistories: (state, action) => {
      state.communicationHistories = [];
    },
  },
  extraReducers: {
    [getOrder.fulfilled]: (state, action) => {
      state.data = action.payload;
    },
    [getCommunicationHistories.fulfilled]: (state, action) => {
      state.communicationHistories = action.payload.notes?.[0] || []
    },
    [getOrderDetails.fulfilled]: (state, action) => {
      state.data = action.payload;
    },
    [saveOrder.fulfilled]: (state, action) => {
      state.loading = false;
      // As of now we are redirecting to another page after successful saveOrder
      // So Removed setting data to avoid displaying nan values
      // state.data = action.payload;
    },
    [saveOrder.rejected]: (state, action) => {
      state.loading = false;
    },
    [requestOrder.pending]: (state, action) => {
      state.disableOrderRequest = true;
    },
    [requestOrder.fulfilled]: (state, action) => {
      state.disableOrderRequest = false;
    },
    [requestOrder.rejected]: (state, action) => {
      state.disableOrderRequest = false;
    },
    [convertToOrder.fulfilled]: (state, action) => {
      state.loading = false;
    },
    [convertToOrder.rejected]: (state, action) => {
      state.loading = false;
    },
    [updateShipmentApproval.fulfilled]: (state) => {
      state.loading = false;
    },
    [updateShipmentApproval.rejected]: (state) => {
      state.loading = false;
    },
    [getQuotedSuppliers.fulfilled]: (state, action) => {
      state.quotedSuppliersList = action.payload;
    },
    [getQuotedSuppliers.pending]: (state) => {
      state.quotedSuppliersList = {}
    },
    [getVasCategoryTypes.fulfilled]: (state, action) => {
      state.vasCategoryTypes = action.payload
    },
    [getVasPriceList.fulfilled]: (state, action) => {
      state.vasPriceLists = action.payload
    },
  },
});

export const {
  resetOrder,
  newOrder,
  openOrderDialog,
  closeOrderDialog,
  openConfirmInvoiceDialog,
  closeConfirmInvoiceDialog,
  openViewOrderDialog,
  closeViewOrderDialog,
  addOrderItem,
  resetOrderItem,
  setLoading,
  resetLoading,
  setPerformingAction,
  resetPerformingAction,
  setQuotedSuppliersListLoading,
  setQuotedSuppliersList,
  openChooseSupplierDialog,
  closeChooseSupplierDialog,
  openSupplierShippingDialog,
  closeSupplierShippingDialog,
  updateQuotedSuppliersList,
  resetCommunicationHistories,
  openVasDialog,
  closeVasDialog
} = orderSlice.actions;

export default orderSlice.reducer;